import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getLoadingAnimation, getAnimationForSurface } from '../../utils/loadingAnimations';

// ─────────────────────────────────────────────────────────────────────────────
// HelixLoader -- the canonical loading animation for the entire app.
//
// Visuals:
//   - Centered Felix character GIF (looped, lightweight)
//   - Cyan conic-gradient "snake" that travels around the border (card variant)
//   - Optional label + elapsed timer
//
// Surface-aware:
//   - surface="timeline"  -> felix-cyan (compact, 16:9-friendly)
//   - surface="canvas"    -> felix-darkblue (subtle, square node cards)
//   - (default)           -> felix-color (full-color, general use)
//
// Variants:
//   - <HelixLoader />              -- small inline loader (40px square)
//   - <HelixLoader size="lg" />    -- fills its container, big video
//   - <HelixLoader showElapsed />  -- shows mm:ss since startedAt
//   - <HelixLoader label="..." />  -- text under the animation
//   - <HelixLoader surface="timeline" /> -- timeline-specific animation
//   - <HelixLoader surface="canvas" />   -- canvas-specific animation
// ─────────────────────────────────────────────────────────────────────────────

const SIZE_CLASSES = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
};

// Isolated elapsed-timer component -- keeps the 1Hz tick from re-rendering
// the surrounding loader (which would force the GIF/video to compete for
// GPU time, the root cause of playback stutter).
const ElapsedTimer = ({ startedAt }) => {
    const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - (startedAt || Date.now())) / 1000));
    useEffect(() => {
        const start = startedAt || Date.now();
        const interval = setInterval(() => {
            setElapsed(Math.floor((Date.now() - start) / 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, [startedAt]);
    return (
        <div className="text-[10px] font-mono text-cyan-300/60">
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
        </div>
    );
};

const HelixLoader = ({
    size = 'md',
    label,
    sublabel,
    showElapsed = false,
    startedAt,
    className = '',
    fill = false,
    animationId,
    surface,            // 'timeline' | 'canvas' -- picks surface-appropriate animation
    videoSrc,           // legacy override
}) => {
    const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

    // Resolve once per mount; 'random' re-rolls on remount but stays stable.
    // Surface prop takes precedence over animationId when both are absent.
    const animation = useMemo(() => {
        if (videoSrc) {
            return { id: 'legacy', src: videoSrc, kind: 'video', compositeMode: 'lighten' };
        }
        if (animationId) return getLoadingAnimation(animationId);
        if (surface) return getAnimationForSurface(surface);
        return getLoadingAnimation();
    }, [animationId, surface, videoSrc]);

    const blend = animation.compositeMode && animation.compositeMode !== 'normal'
        ? { mixBlendMode: animation.compositeMode }
        : undefined;

    return (
        <div
            className={`relative flex flex-col items-center justify-center gap-2 ${fill ? 'w-full h-full' : ''} ${className}`}
        >
            {animation.kind === 'gif' || animation.kind === 'image' ? (
                <img
                    src={animation.src}
                    alt={animation.label || 'Loading'}
                    className={`${sizeClass} object-contain`}
                    style={blend}
                    draggable={false}
                />
            ) : (
                <video
                    src={animation.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className={`${sizeClass} object-contain`}
                    style={blend}
                />
            )}
            {label && (
                <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-100 text-center px-2">
                    {label}
                </div>
            )}
            {sublabel && (
                <div className="text-[9px] text-amber-300/80 text-center px-3 leading-snug max-w-[18ch]">
                    {sublabel}
                </div>
            )}
            {showElapsed && <ElapsedTimer startedAt={startedAt} />}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// HelixLoaderCard -- the loader inside a card-shaped container with the
// rotating conic-gradient border. Used by PendingCard, generation tiles,
// any "I'm working on this" placeholder.
// ─────────────────────────────────────────────────────────────────────────────

export const HelixLoaderCard = React.forwardRef(({
    label,
    sublabel,
    showElapsed = false,
    startedAt,
    aspectClass = 'aspect-square',
    radius = 'rounded-xl',
    surface,
    children,
    className = '',
}, ref) => {
    return (
        <motion.div
            ref={ref}
            layout
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.2 }}
            className={`relative ${aspectClass} ${radius} overflow-hidden ring-1 ring-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-cyan-500/10 shadow-[0_0_24px_rgba(24,181,240,0.18)] ${className}`}
        >
            {/* Diagonal shimmer sweep */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute inset-0 -translate-x-full"
                    style={{
                        background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                        animation: 'shimmer 1.6s linear infinite',
                    }}
                />
            </div>

            {/* Pulsing radial glow */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: 'radial-gradient(circle at center, rgba(24,181,240,0.18) 0%, transparent 60%)',
                    animation: 'pulse 2s ease-in-out infinite',
                }}
            />

            {/* Centered Felix + label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 py-3">
                <HelixLoader
                    size="xl"
                    label={label}
                    sublabel={sublabel}
                    showElapsed={showElapsed}
                    startedAt={startedAt}
                    surface={surface}
                />
            </div>

            {/* Bottom progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500/20 overflow-hidden pointer-events-none">
                <div
                    className="h-full w-1/3 bg-cyan-300"
                    style={{ animation: 'slide 1.4s ease-in-out infinite' }}
                />
            </div>

            {children}
        </motion.div>
    );
});
HelixLoaderCard.displayName = 'HelixLoaderCard';

export default HelixLoader;
